export const CALENDAR_CONNECTOR = {
	google: 'google-calendar-connector',
	outlook: 'outlook-calendar-connector',
} as const;

export type CalendarConnectorType = (typeof CALENDAR_CONNECTOR)[keyof typeof CALENDAR_CONNECTOR];

export const CALENDAR_CONNECTOR_TYPES: readonly CalendarConnectorType[] =
	Object.values(CALENDAR_CONNECTOR);

/**
 * A connected data source for an org, from
 * `GET /gateway/api/third-party-configuration/connected-data-sources/{cloudId}`.
 */
export type ConnectedDataSource = {
	type: string;
};

export type CalendarConnectionStatus = 'pending' | 'authed' | 'unauthed' | 'failed';

export type CalendarEvent = {
	id: string;
	summary: string;
	type: 'all-day' | 'timed';
	zonedStart: string;
	zonedEnd: string;
	meetingUrl?: string;
	source?: CalendarConnectorType;
	description?: string;
	attendees?: CalendarEventAttendee[];
};

export type CalendarEventAttendee = {
	email: string;
};

/** A `CalendarEvent` on one day of the strip, with whole calendar days from that day to its start and end. */
export type DayEvent = CalendarEvent & { startsDaysBefore: number; endsDaysAfter: number };

/**
 * One row's countdown.
 *
 * - `label`: what the row says.
 * - `action`: what the event qualifies for. The trailing component decides the button's look.
 */
export type EventCountdown = { eventId: string; label: string; action: 'join' | 'prepare' };

export type EntityDateTime = { dateTime?: string; date?: string; timeZone?: string };

// GOOGLE
export type GoogleEventsInputs = {
	calendarId: string;
	timeMin: string;
	timeMax: string;
	maxResults: number;
	timeZone: string;
};

export type GoogleEventAttendee = {
	self?: boolean; // used to identify the current user
	responseStatus: string;
	email?: string;
};

export type GoogleEventEntity = {
	id?: string;
	eventType?: string;
	summary?: string;
	description?: string; // HTML, not plain text
	start?: EntityDateTime;
	end?: EntityDateTime;
	location?: string;
	videoMeetingUrl?: string;
	attendees?: GoogleEventAttendee[];
};

export type GoogleEventsWrapper = {
	events?: GoogleEventEntity[];
};

// OUTLOOK
export type OutlookEventsInputs = {
	startDateTime: string;
	endDateTime: string;
	top: number;
	timeZone: string;
};

export type OutlookEventAttendee = {
	email?: string;
};

export type OutlookEventEntity = {
	id?: string;
	subject?: string;
	showAs?: string;
	start?: EntityDateTime;
	end?: EntityDateTime;
	isAllDay?: boolean;
	onlineMeeting?: { joinUrl?: string } | null;
	currentUserResponseStatus?: string;
	bodyPreview?: string;
	body?: string;
	bodyContentType?: string;
	attendees?: OutlookEventAttendee[];
};

// REQUEST
export type CalendarRawEvent = GoogleEventEntity | OutlookEventEntity;

export type CalendarActionError = {
	message?: string;
	statusCode?: number;
	extensions?: {
		authUrl?: string;
		errorType?: string;
		statusCode?: number;
	};
};

export type CalendarActionOutputs = {
	operationStatus?: string;
	errors?: CalendarActionError[];
	entities?: unknown;
	httpStatus?: string;
};

export type ActionRequest = {
	actionId: string;
	integrationKey: string;
	inputs: GoogleEventsInputs | OutlookEventsInputs;
};
